import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationPermissionModal = ({ isOpen, onClose, onAllow, onSkip }) => {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleAllowLocation = async () => {
    setIsRequesting(true);
    
    try {
      if (navigator.geolocation) {
        navigator.geolocation?.getCurrentPosition(
          (position) => {
            const location = {
              latitude: position?.coords?.latitude,
              longitude: position?.coords?.longitude,
              accuracy: position?.coords?.accuracy
            };
            setIsRequesting(false);
            onAllow(location);
          },
          (error) => {
            console.error('Location error:', error);
            setIsRequesting(false);
            onSkip();
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      } else {
        setIsRequesting(false);
        onSkip();
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      setIsRequesting(false);
      onSkip();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal Content */}
      <div className="relative bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
            <Icon name="MapPin" size={32} className="text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">
            Enable Location Services
          </h3>
          <p className="text-muted-foreground">
            Allow ServiceWatch to access your location for more accurate outage reporting and personalized alerts.
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-3 mb-6">
          <div className="flex items-start space-x-3">
            <div className="flex items-center justify-center w-6 h-6 bg-success/10 rounded-full mt-0.5">
              <Icon name="Check" size={14} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Local Outage Alerts</p>
              <p className="text-xs text-muted-foreground">Get notified about outages in your area</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex items-center justify-center w-6 h-6 bg-success/10 rounded-full mt-0.5">
              <Icon name="Check" size={14} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Accurate Reporting</p>
              <p className="text-xs text-muted-foreground">Help others by reporting outages with precise location</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex items-center justify-center w-6 h-6 bg-success/10 rounded-full mt-0.5">
              <Icon name="Check" size={14} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Personalized Dashboard</p>
              <p className="text-xs text-muted-foreground">See relevant outages and service status</p>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-muted/50 rounded-lg p-3 mb-6">
          <div className="flex items-start space-x-2">
            <Icon name="Shield" size={16} className="text-muted-foreground mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Privacy Protected:</span> Your location is used only for outage monitoring and is never shared with third parties.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleAllowLocation}
            loading={isRequesting}
            disabled={isRequesting}
            iconName="MapPin"
            iconPosition="left"
          >
            {isRequesting ? 'Getting Location...' : 'Allow Location Access'}
          </Button>

          <Button
            variant="ghost"
            size="lg"
            fullWidth
            onClick={onSkip}
            disabled={isRequesting}
          >
            Skip for Now
          </Button>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors"
          disabled={isRequesting}
        >
          <Icon name="X" size={16} />
        </button>
      </div>
    </div>
  );
};

export default LocationPermissionModal;